import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { HamburgerIcon } from "@chakra-ui/icons";
import {
	Box,
	Button,
	Flex,
	HStack,
	Icon,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	SimpleGrid,
	Td,
	Text,
	Tr,
	useColorModeValue,
	useDisclosure,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiFileText, FiHash, FiInfo, FiTrendingUp } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "../PropertyModal/shared";
import { removeDocumentAsync, updateDocumentActiveAsync } from "../../redux/user/userSlice";
import { API } from "../../utils/http";
import { fullDateFormat, fullDateTimeFormat } from "../../utils/types";
import { getDocumentBadge } from "../../utils/utils";

export default function LeaseTableRow(props) {
	const { row, propertyId, payments } = props;
	const [rentAmount, setRentAmount] = useState({ ron: 0, eur: 0 });
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const textColor = useColorModeValue("gray.700", "white");
	const iconColor = useColorModeValue("blue.500", "blue.300");

	const deleteDocument = (documentId) => {
		console.log("delete: ", { documentId, propertyId });
		dispatch(removeDocumentAsync({ documentId, propertyId }));
	};
	const getRentAmount = (start, end) => {
		console.log(`getting amount from ${start} to ${end}`);

		const rentPayments = payments.filter(
			(p) => p.type === "rent" && moment(p.issuedDate).unix() > moment(start).unix() && moment(p.issuedDate).unix() < moment(end).unix()
		);
		console.log(rentPayments);
		const amountInRon = rentPayments.filter((p) => p.currency === "ron").reduce((accumulator, currentValue) => accumulator + Number(currentValue.amount), 0);
		const amountInEur = rentPayments.filter((p) => p.currency === "eur").reduce((accumulator, currentValue) => accumulator + Number(currentValue.amount), 0);
		console.log(amountInRon);
		console.log(amountInEur);
		setRentAmount({ eur: amountInEur, ron: amountInRon });
		onOpen();
	};
	const getDocument = (documentId, documentName) => {
		const payload = { documentId, propertyId, documentName };
		console.log("view: ", payload);
		API.put("get-document", payload).then((r) => {
			console.log(r);
			window.open(r.data.url, "_blank");
		});
	};
	const markAsActive = (documentId) => {
		const payload = { documentId, propertyId, type: row.type, active: true };
		console.log("mark document as active: ", payload);
		dispatch(updateDocumentActiveAsync(payload));
	};
	const markAsInactive = (documentId) => {
		const payload = { documentId, propertyId, type: row.type, active: false };
		console.log("mark document as active: ", payload);
		dispatch(updateDocumentActiveAsync(payload));
	};

	const handleCloseRentModal = () => {
		onClose();
		setRentAmount({ eur: 0, ron: 0 });
	};

	const renderRentAmountModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseRentModal}
			size="3xl"
			icon={FiTrendingUp}
			title={t("property.rentRevenueSummaryTitle")}
			subtitle={t("property.rentRevenueSummarySubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseRentModal} cancelLabel={t("misc.close")} />}
		>
			<SimpleGrid columns={{ base: 1, md: 2 }} spacing={MODAL_GRID_SPACING}>
				<PropertyModalSection icon={FiCalendar} title={t("property.leasePeriod")}>
					<VStack spacing={3} align="stretch">
						<Box>
							<HStack spacing={2} mb={2}>
								<Icon as={FiCalendar} color={iconColor} boxSize={4} />
								<Text fontSize="sm" fontWeight="medium">
									{t("property.startDate")}
								</Text>
							</HStack>
							<Text fontSize="sm" fontWeight="medium">
								{moment(row.startingDate).format(fullDateFormat)}
							</Text>
						</Box>
						<Box>
							<HStack spacing={2} mb={2}>
								<Icon as={FiCalendar} color={iconColor} boxSize={4} />
								<Text fontSize="sm" fontWeight="medium">
									{t("property.endDate")}
								</Text>
							</HStack>
							<Text fontSize="sm" fontWeight="medium">
								{moment(row.expirationDate).format(fullDateFormat)}
							</Text>
						</Box>
						<Box>
							<HStack spacing={2} mb={2}>
								<Icon as={FiFileText} color={iconColor} boxSize={4} />
								<Text fontSize="sm" fontWeight="medium">
									{t("table.name")}
								</Text>
							</HStack>
							<Text fontSize="sm" fontWeight="medium" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
								{row.number}
							</Text>
						</Box>
					</VStack>
				</PropertyModalSection>

				<PropertyModalSection icon={FiHash} title={t("property.generatedRevenue")}>
					<VStack spacing={3} align="stretch">
						<Box>
							<HStack spacing={2} mb={2}>
								<Icon as={FiHash} color="green.500" boxSize={4} />
								<Text fontSize="sm" fontWeight="medium">
									{t("property.eurRevenue")}
								</Text>
							</HStack>
							<Text fontSize="lg" fontWeight="bold" color="green.500">
								{rentAmount.eur.toFixed(2)} EUR
							</Text>
						</Box>
						<Box>
							<HStack spacing={2} mb={2}>
								<Icon as={FiHash} color="blue.500" boxSize={4} />
								<Text fontSize="sm" fontWeight="medium">
									{t("property.ronRevenue")}
								</Text>
							</HStack>
							<Text fontSize="lg" fontWeight="bold" color="blue.500">
								{rentAmount.ron.toFixed(2)} RON
							</Text>
						</Box>
						<Box>
							<HStack spacing={2} mb={2}>
								<Icon as={FiInfo} color={iconColor} boxSize={4} />
								<Text fontSize="sm" fontWeight="medium">
									{t("property.totalRevenue")}
								</Text>
							</HStack>
							<Text fontSize="lg" fontWeight="bold">
								{(rentAmount.eur + rentAmount.ron).toFixed(2)} Total
							</Text>
						</Box>
					</VStack>
				</PropertyModalSection>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Tr>
			<Td minWidth={{ sm: "200px" }}>
				<Flex direction={"column"}>
					<Text fontSize="md" color={textColor}>
						{moment(row.addedDate).format(fullDateTimeFormat)}
					</Text>
					{getDocumentBadge(row)}
				</Flex>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.name}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{t(`types.${row.type}`)}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.number}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem" whiteSpace={"nowrap"}>
					{row.signatureDate ? moment(row.signatureDate).format(fullDateFormat) : "-"}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem" whiteSpace={"nowrap"}>
					{row.startingDate ? moment(row.startingDate).format(fullDateFormat) : "-"}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem" whiteSpace={"nowrap"}>
					{row.expirationDate ? moment(row.expirationDate).format(fullDateFormat) : "-"}
				</Text>
			</Td>
			<Td>
				<Menu closeOnSelect={false}>
					<MenuButton>
						<Icon as={HamburgerIcon} />
					</MenuButton>
					<MenuList>
						<MenuItem
							onClick={() => {
								getDocument(row.id, row.name);
							}}
						>
							Download
						</MenuItem>
						{row.active ? (
							<MenuItem
								onClick={() => {
									markAsInactive(row.id);
								}}
							>
								Mark as inactive
							</MenuItem>
						) : (
							<MenuItem
								onClick={() => {
									markAsActive(row.id);
								}}
							>
								Mark as Active
							</MenuItem>
						)}

						<MenuItem
							onClick={() => {
								deleteDocument(row.id);
							}}
						>
							Delete
						</MenuItem>
						<MenuItem
							onClick={() => {
								getRentAmount(row.startingDate, row.expirationDate);
							}}
						>
							Get Rent Amount
						</MenuItem>
					</MenuList>
				</Menu>
				{renderRentAmountModal()}
			</Td>
		</Tr>
	);
}
